import { queryClient } from '@hotjar/webapp/utils/react-query';
import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';
import type { Site } from '@hotjar/site';
import type { ToolType } from './ToolsCountResult';
import { getToolsCount } from './api';

const FEEDBACK_TOOLS_QUERY_BASE_KEY = 'feedback_tools';
const FEEDBACK_TOOLS_STALE_TIME = 1000 * 60 * 5;

export const useToolsCountQuery = <T extends ToolType>(
  site?: Site,
  toolType?: T,
): UseQueryResult<number, Error> => {
  return useQuery(
    [FEEDBACK_TOOLS_QUERY_BASE_KEY, { siteId: site?.id, toolType }],
    async () => {
      if (!site) throw new Error('Missing site');
      if (!toolType) throw new Error('Missing toolType');

      if (toolType === 'feedback') {
        const toolsCount = await getToolsCount(site, 'feedback');
        return toolsCount.n_incoming_feedback;
      }
      if (toolType === 'surveys') {
        const toolsCount = await getToolsCount(site, 'surveys');
        return toolsCount.n_polls;
      }

      const toolsCount = await getToolsCount(site, 'all');
      return toolsCount.n_polls + toolsCount.n_incoming_feedback;
    },
    {
      enabled: Boolean(site?.id) && Boolean(toolType),
      staleTime: FEEDBACK_TOOLS_STALE_TIME,
    },
  );
};

export const invalidateFeedbackToolsCount = (siteId: number, toolType: ToolType): void => {
  if (toolType === 'all') {
    queryClient.invalidateQueries([FEEDBACK_TOOLS_QUERY_BASE_KEY, { siteId }]);
    return;
  }

  queryClient.invalidateQueries([FEEDBACK_TOOLS_QUERY_BASE_KEY, { siteId, toolType: 'all' }]);
  queryClient.invalidateQueries([FEEDBACK_TOOLS_QUERY_BASE_KEY, { siteId, toolType }]);
};
